import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { BsFillGrid3X3GapFill } from 'react-icons/bs'
import { IoPersonCircleOutline } from 'react-icons/io5'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { isAuthenticated, logout } from '../../../shared/utils/authUtils'
import useAppSelector from '../../../state/redux/hooks/index'
import { fetchNotifications } from '../../../state/redux/actions/notificationActions.js'
import { fetchAdminProfile } from '../../../state/redux/actions/userActions.js'
import { formatTime } from '../../../shared/utils/dateUtils'

const Header = () => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false)
  const [authToken, setAuthToken] = useState(false)
  const [imageError, setImageError] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { notifications, loading, error } = useAppSelector(state => state.notifications)
  const { adminProfile, adminProfileLoading } = useAppSelector(state => state.user)
  const [localNotifications, setLocalNotifications] = useState([])

  // Get profile photo URL from admin profile
  const getProfilePhotoUrl = () => {
    if (adminProfile?.profile_photo) {
      // If it's already a full URL, use it
      if (adminProfile.profile_photo.startsWith('http')) {
        return adminProfile.profile_photo
      }
      // Otherwise, construct the full URL
      return `https://swap-space-latest.onrender.com${adminProfile.profile_photo}`
    }
    return null
  }

  useEffect(() => {
    setAuthToken(isAuthenticated())
    const handleStorageChange = () => {
      setAuthToken(isAuthenticated())
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    if (isAuthenticated()) {
      dispatch(fetchNotifications({ limit: 10, page: 1 }))
      // Fetch admin profile which includes profile photo
      dispatch(fetchAdminProfile())
    }
  }, [dispatch])

  // Reset image error when admin profile changes
  useEffect(() => {
    setImageError(false)
  }, [adminProfile?.profile_photo])

  // Listen for profile photo updates and refresh admin profile
  useEffect(() => {
    const handleProfilePhotoUpdate = () => {
      // Refresh admin profile when photo is updated
      dispatch(fetchAdminProfile())
    }

    window.addEventListener('profilePhotoUpdated', handleProfilePhotoUpdate)
    return () => {
      window.removeEventListener('profilePhotoUpdated', handleProfilePhotoUpdate)
    }
  }, [dispatch])

  useEffect(() => {
    if (notifications) {
      setLocalNotifications(notifications)
    }
  }, [notifications])


  const handleLogout = () => {
    logout()
    setAuthToken(false)
    navigate('/login')
  }

  const handleNotificationClick = () => {
    setIsNotificationMenuOpen(!isNotificationMenuOpen)
    setIsProfileMenuOpen(false)
  }

  const handleProfileClick = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen)
    setIsNotificationMenuOpen(false)
  }

  const markAsRead = notificationId => {
    setLocalNotifications(prev =>
      prev.map(notification =>
        notification._id === notificationId
          ? { ...notification, engagement: { ...notification.engagement, isOpened: true } }
          : notification
      )
    )
  }

  const hasUnreadNotifications = localNotifications.some(notification => !notification.engagement?.isOpened)

  const renderProfileAvatar = () => {
    const profilePhotoUrl = getProfilePhotoUrl()

    if (adminProfileLoading) {
      return (
        <div className="w-6 h-6 rounded-full bg-gray-300 animate-pulse flex items-center justify-center">
          <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
        </div>
      )
    }

    if (profilePhotoUrl && !imageError) {
      return (
        <div className="w-6 h-6 rounded-full relative overflow-hidden border-2 border-white shadow-sm">
          <img
            src={profilePhotoUrl}
            alt="Profile"
            className="w-full h-full object-cover"
            onError={() => {
              console.error('Image failed to load:', profilePhotoUrl)
              setImageError(true)
            }}
            onLoad={() => {
              console.log('Profile image loaded successfully')
              setImageError(false)
            }}
          />
        </div>
      )
    }

    if (profilePhotoUrl && imageError) {
      return (
        <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center border-2 border-white shadow-sm">
          <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
          </svg>
        </div>
      )
    }

    return <IoPersonCircleOutline className="text-xl" />
  }

  return (
    <header className="ml-[250px] mt-4 w-[1040px] z-50">
      <div className="py-4 px-8 flex justify-end items-center h-12">
        <div className="items-center flex justify-end gap-6">
          <div className="relative">
            <div
              className="items-center py-2 px-4 text-gray-700 cursor-pointer relative"
              onClick={handleNotificationClick}
            >
              <span className="relative">
                <i className="fa-solid fa-bell" />
                {hasUnreadNotifications && (
                  <div className="absolute -top-2 -right-2 w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                )}
              </span>
            </div>

            {isNotificationMenuOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50">
                <div className="p-3 border-b border-gray-200">
                  <h3 className="font-medium text-gray-800">Notifications</h3>
                </div>

                <div className="max-h-80 overflow-hidden scrollbar-hide">
                  {loading && (
                    <div className="py-4 px-4 text-center text-gray-500 text-sm">Loading notifications...</div>
                  )}

                  {error && (
                    <div className="py-4 px-4 text-center text-red-500 text-sm">
                      Failed to load notifications: {error}
                    </div>
                  )}

                  {!loading && !error && localNotifications && localNotifications.length > 0 ? (
                    <ul className="py-1">
                      {localNotifications.map(notification => (
                        <li
                          key={notification._id}
                          className={`px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 ${
                            !notification.engagement?.isOpened ? 'bg-teal-50' : 'bg-white'
                          }`}
                          onClick={() => markAsRead(notification._id)}
                        >
                          <div className="flex justify-between">
                            <p className="text-sm font-medium text-gray-800">{notification.type}</p>
                            {!notification.engagement?.isOpened && (
                              <span className="w-2 h-2 bg-teal-500 rounded-full ml-2 mt-2"></span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{formatTime(notification.timestamp)}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    !loading &&
                    !error && <div className="py-4 px-4 text-center text-gray-500 text-sm">No notifications</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-4 text-white text-base px-6 py-3 bg-primary rounded-md">
            <div className="items-center">
              <BsFillGrid3X3GapFill className="text-lg" />
            </div>

            <div className="relative flex items-center cursor-pointer" onClick={handleProfileClick}>
              <div className="relative">{renderProfileAvatar()}</div>
              {isProfileMenuOpen && (
                <div className="min-w-52 absolute block top-10 right-0 bg-white border border-border rounded-md shadow-sm mt-4 z-50">
                  <ul className="py-2 px-0 m-0 list-none">
                    <li>
                      <Link
                        to="/settings"
                        className="block py-3 px-6 text-dark no-underline text-sm whitespace-nowrap hover:bg-slate-100"
                      >
                        My Profile
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/settings"
                        className="block py-3 px-6 text-dark no-underline text-sm whitespace-nowrap hover:bg-slate-100"
                      >
                        Settings
                      </Link>
                    </li>
                    <li>
                      {authToken ? (
                        <button
                          onClick={handleLogout}
                          className="text-sm whitespace-nowrap text-danger border border-border mt-2 text-left bg-transparent py-3 px-6 w-full inline-block cursor-pointer hover:bg-slate-100"
                        >
                          Logout
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate('/login')}
                          className="text-sm whitespace-nowrap text-dark border border-border mt-2 text-left bg-transparent py-3 px-6 w-full inline-block cursor-pointer hover:bg-teal-400/10"
                        >
                          Login
                        </button>
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
